"use client";

import { adjustNavbarScrollOffset } from "@/lib/navbar-scroll-offset";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { List } from "lucide-react";
import { useArticleRect } from "@/hooks/useArticleRect";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  MobileBottomToolRegistration,
  useMobileBottomTools,
} from "@/components/mobile-bottom-tools";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { PASS_FEATURES } from "@/types/pass";
import { isExamInfoPage, isExamShellPage } from "@/lib/route-layout";
import {
  SECTION_LEVELS,
  SECTION_LEVEL_STYLES,
  type SectionLevelName,
} from "@/lib/section-levels";

const TOC_VISIBILITY_KEY = "tocVisible";
const TOC_ICON_POSITION_KEY = "tocIconPosition";

const LANG_CLASSES = ["c", "java", "python"] as const;

function normalizePath(path: string): string {
  if (!path || path === "/") return "/";
  return path.replace(/\/+$/, "");
}

function getCurrentCodeLang(): string {
  return document.documentElement.getAttribute("data-code-lang") || "c";
}

function isVisibleElement(element: Element): boolean {
  let current: Element | null = element;

  while (current && current !== document.documentElement) {
    if (current instanceof HTMLElement && current.hidden) return false;

    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }

    current = current.parentElement;
  }

  return true;
}

function findCurrentArticle(pagePath: string): HTMLElement | null {
  const normalizedPagePath = normalizePath(pagePath);
  const visibleArticles = Array.from(
    document.querySelectorAll<HTMLElement>("article")
  ).filter(isVisibleElement);

  return (
    visibleArticles.find(
      (article) =>
        normalizePath(article.dataset.pagePath || "") === normalizedPagePath
    ) ??
    visibleArticles[0] ??
    null
  );
}

function isHeadingInCurrentLanguage(
  heading: Element,
  currentLang: string
): boolean {
  const langContainer = heading.closest(
    LANG_CLASSES.map((l) => `.${l}-only`).join(", ")
  );

  if (!langContainer) return true;

  return langContainer.classList.contains(`${currentLang}-only`);
}

function findHeadingInArticle(
  article: HTMLElement,
  id: string,
  currentLang: string
): HTMLElement | null {
  return (
    Array.from(article.querySelectorAll<HTMLElement>("h2, h3")).find(
      (heading) =>
        heading.id === id &&
        isVisibleElement(heading) &&
        isHeadingInCurrentLanguage(heading, currentLang)
    ) ?? null
  );
}

function getHeadingTextForToc(heading: Element): string {
  const clone = heading.cloneNode(true) as Element;
  clone
    .querySelectorAll("[data-section-level], [data-llm-copy-control]")
    .forEach((el) => el.remove());
  return clone.textContent || "";
}

interface TocItem {
  id: string;
  text: string;
  level: number; // 2 for h2, 3 for h3
  badge?: SectionLevelName;
}

interface TableOfContentsProps {
  pagePath?: string;
}

export default function TableOfContents({ pagePath }: TableOfContentsProps = {}) {
  const pathname = usePathname() ?? "";
  const currentPath = normalizePath(pathname);
  const ownPath = normalizePath(pagePath ?? currentPath);
  const isCurrentPage = currentPath === ownPath;
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const [hasSpace, setHasSpace] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [forcedOpen, setForcedOpen] = useState<boolean>(false);
  const [tocRight, setTocRight] = useState<number>(0);
  const articleRect = useArticleRect();
  const iconRef = useRef<HTMLButtonElement>(null);
  const [iconPosition, setIconPosition] = useState<{ x: number; y: number } | null>(null);
  const dragState = useRef({
    isDragging: false,
    hasMoved: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const handleIconClickRef = useRef<() => void>(() => {});
  const articleRef = useRef<HTMLElement | null>(null);

  // 3컬럼 셸(UI 2.0) 좌측 사이드바 슬롯. Provider 없으면 inShell:false → 기존 플로팅 동작 유지.
  // 셸에서 목차 Section 펼침/스플릿 높이는 ArticleShell 이 소유하고, 여기서는 리스트만 portal.
  const { inShell, tocSlot } = useArticleShellSlots();
  const { openToolId, closeSheet } = useMobileBottomTools();
  const { isLoaded: passLoaded, hasAccess } = useEffectivePass();
  // useMemo 유지: ownPath 를 렌더 식에서 직접 읽으면 React Compiler 가 아래 useCallback 들의
  // setState 의존성을 추론하지 못해 preserve-manual-memoization 오류(6건)를 낸다(JCG-3583 도입).
  const hasDesktopTocAccess = useMemo(
    () =>
      isExamInfoPage(ownPath) ||
      (passLoaded && hasAccess(PASS_FEATURES.tableOfContents)),
    [ownPath, passLoaded, hasAccess]
  );
  const shouldUseProblemListInstead = isExamShellPage(pathname);

  const getScopedArticle = useCallback(() => {
    const currentArticle = articleRef.current;
    if (
      currentArticle &&
      isVisibleElement(currentArticle) &&
      normalizePath(currentArticle.dataset.pagePath || "") === ownPath
    ) {
      return currentArticle;
    }

    const article = findCurrentArticle(ownPath);
    articleRef.current = article;
    return article;
  }, [ownPath]);

  // article 위치 기반으로 공간 확인 및 TOC 위치 계산 — DOM 측정 기반 외부 동기화
  useEffect(() => {
    if (!articleRect || articleRect.viewportWidth < 1280) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasSpace(false);
      if (!articleRect || articleRect.viewportWidth < 1280) setForcedOpen(false);
      return;
    }

    const tocWidth = articleRect.viewportWidth >= 1536 ? 208 : 160; // 2xl:w-52 : w-40
    const gap = 40;
    const requiredSpace = tocWidth + gap + 16; // TOC 너비 + 간격 + 최소 좌측 여백

    // article 좌측 기준으로 TOC right 위치 계산
    setTocRight(articleRect.viewportWidth - articleRect.left + gap);

    const newHasSpace = articleRect.left >= requiredSpace;
    setHasSpace(newHasSpace);
    if (newHasSpace) setForcedOpen(false);
  }, [articleRect]);

  // localStorage에서 표시 상태 불러오기 — 외부 시스템 동기화
  useEffect(() => {
    const saved = localStorage.getItem(TOC_VISIBILITY_KEY);
    if (saved !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsVisible(saved === "true");
    }
  }, []);

  // 현재 페이지의 보이는 article 안에서만 h2, h3 요소 추출 (언어별 필터링 포함)
  const extractHeadings = useCallback(() => {
    if (!isCurrentPage) {
      articleRef.current = null;
      setItems([]);
      setActiveId("");
      return;
    }

    const currentLang = getCurrentCodeLang();
    const article = getScopedArticle();
    if (!article) {
      setItems([]);
      setActiveId("");
      return;
    }

    const headings = article.querySelectorAll("h2, h3");
    const tocItems: TocItem[] = [];

    headings.forEach((heading) => {
      const id = heading.id;
      const text = getHeadingTextForToc(heading);
      const level = heading.tagName === "H2" ? 2 : 3;

      // SectionLevel 뱃지 텍스트 분리
      let badge: SectionLevelName | undefined;
      let cleanText = text;
      for (const badgeLevel of SECTION_LEVELS) {
        if (text.endsWith(badgeLevel)) {
          badge = badgeLevel;
          cleanText = text.slice(0, -badgeLevel.length).trim();
          break;
        }
      }

      // 각주 섹션 제외
      const isInFootnotesSection =
        heading.closest(".footnotes") !== null ||
        heading.closest('[data-footnotes="true"]') !== null;
      const isFootnotesHeading = id === "footnotes";

      if (!id || !cleanText || isInFootnotesSection || isFootnotesHeading)
        return;

      if (
        !isVisibleElement(heading) ||
        !isHeadingInCurrentLanguage(heading, currentLang)
      )
        return;

      tocItems.push({ id, text: cleanText, level, badge });
    });

    setItems(tocItems);

    // 초기 활성 섹션 설정
    setActiveId((prev) =>
      tocItems.some((item) => item.id === prev) ? prev : (tocItems[0]?.id ?? "")
    );
  }, [getScopedArticle, isCurrentPage]);

  // 초기/경로 변경 시 헤딩 추출 (HashHighlighter의 ID 패치 이후)
  useEffect(() => {
    articleRef.current = null;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItems([]);
    setActiveId("");
    if (!isCurrentPage) return;

    // HashHighlighter가 먼저 ID를 패치하므로 약간의 지연 필요
    const timer = setTimeout(extractHeadings, 50);
    return () => clearTimeout(timer);
  }, [extractHeadings, isCurrentPage]);

  // 언어 전환 시 헤딩 목록 갱신
  useEffect(() => {
    if (!isCurrentPage) return;

    const handleLangSwitch = () => {
      // ID 패치 및 CSS 적용 대기 후 갱신
      setTimeout(extractHeadings, 50);
    };

    window.addEventListener("lang-switch", handleLangSwitch);
    return () => window.removeEventListener("lang-switch", handleLangSwitch);
  }, [extractHeadings, isCurrentPage]);

  // Intersection Observer로 현재 보이는 섹션 감지
  useEffect(() => {
    if (!isCurrentPage || items.length === 0) return;

    const article = getScopedArticle();
    if (!article) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // 뷰포트에 들어온 요소들 중 가장 위에 있는 요소 찾기
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          // boundingClientRect.top이 가장 작은 (가장 위에 있는) 요소 선택
          const topEntry = visibleEntries.reduce((prev, current) =>
            prev.boundingClientRect.top < current.boundingClientRect.top
              ? prev
              : current
          );
          setActiveId(topEntry.target.id);
        }
      },
      {
        rootMargin: "-80px 0px -70% 0px", // 상단 80px(네비게이션 높이), 하단 70% 영역 제외
        threshold: 0,
      }
    );

    // 모든 헤딩 요소 관찰
    const currentLang = getCurrentCodeLang();
    items.forEach((item) => {
      const element = findHeadingInArticle(article, item.id, currentLang);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [getScopedArticle, isCurrentPage, items]);

  const scrollToHeading = useCallback(
    (id: string) => {
      const article = getScopedArticle();
      const element = article
        ? findHeadingInArticle(article, id, getCurrentCodeLang())
        : null;
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const navbarHeight = adjustNavbarScrollOffset(120, elementPosition + window.scrollY); // 네비 + StickyTitle
        const offsetPosition =
          elementPosition + window.pageYOffset - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        // URL 해시 업데이트
        window.history.pushState(null, "", `#${id}`);
        setActiveId(id);
      }
    },
    [getScopedArticle]
  );

  // 모바일 시트는 body 를 fixed 로 잠근다. 닫힌 뒤 원래 스크롤 위치가 복원되어야
  // heading 의 문서 좌표를 정확히 계산하고 Safari 에서도 스크롤할 수 있다.
  useEffect(() => {
    if (!pendingScrollId || openToolId === "toc") return;

    let frame: number;
    const scrollWhenUnlocked = () => {
      if (document.documentElement.hasAttribute("data-scroll-locked")) {
        frame = requestAnimationFrame(scrollWhenUnlocked);
        return;
      }
      scrollToHeading(pendingScrollId);
      setPendingScrollId(null);
    };
    frame = requestAnimationFrame(scrollWhenUnlocked);
    return () => cancelAnimationFrame(frame);
  }, [openToolId, pendingScrollId, scrollToHeading]);

  // 클릭 시 해당 섹션으로 스크롤
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      if (!isCurrentPage) return;

      if (openToolId === "toc") {
        setPendingScrollId(id);
        closeSheet();
      } else {
        scrollToHeading(id);
      }
    },
    [closeSheet, isCurrentPage, openToolId, scrollToHeading]
  );

  // 표시/숨김 토글
  const toggleVisibility = useCallback(() => {
    setIsVisible((prev) => {
      const next = !prev;
      localStorage.setItem(TOC_VISIBILITY_KEY, String(next));
      return next;
    });
  }, []);

  // 아이콘 위치 불러오기 — 외부 localStorage 동기화
  useEffect(() => {
    const saved = localStorage.getItem(TOC_ICON_POSITION_KEY);
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        const btnSize = 48;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIconPosition({
          x: Math.min(Math.max(0, pos.x), window.innerWidth - btnSize),
          y: Math.min(Math.max(0, pos.y), window.innerHeight - btnSize),
        });
      } catch {
        setIconPosition(null);
      }
    }
  }, []);

  // 아이콘 클릭 핸들러 ref 업데이트
  useEffect(() => {
    handleIconClickRef.current = () => {
      if (hasSpace) {
        toggleVisibility();
      } else {
        setForcedOpen(true);
      }
    };
  }, [hasSpace, toggleVisibility]);

  // 드래그 핸들러
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    const btn = iconRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    dragState.current = {
      isDragging: true,
      hasMoved: false,
      startX: clientX,
      startY: clientY,
      offsetX: clientX - rect.left,
      offsetY: clientY - rect.top,
    };
  }, []);

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    const state = dragState.current;
    if (!state.isDragging) return;
    const dx = clientX - state.startX;
    const dy = clientY - state.startY;
    if (!state.hasMoved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
    state.hasMoved = true;
    const btnSize = 48;
    const newX = Math.min(Math.max(0, clientX - state.offsetX), window.innerWidth - btnSize);
    const newY = Math.min(Math.max(0, clientY - state.offsetY), window.innerHeight - btnSize);
    setIconPosition({ x: newX, y: newY });
  }, []);

  const handleDragEnd = useCallback(() => {
    const state = dragState.current;
    if (state.isDragging && state.hasMoved) {
      const btn = iconRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const pos = { x: rect.left, y: rect.top };
        setIconPosition(pos);
        try { localStorage.setItem(TOC_ICON_POSITION_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
      }
    }
    dragState.current = { ...state, isDragging: false, hasMoved: false };
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  }, [handleDragStart]);

  // 마우스 이벤트
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.current.isDragging) return;
      handleDragMove(e.clientX, e.clientY);
    };
    const onMouseUp = () => {
      if (!dragState.current.isDragging) return;
      const wasDragged = dragState.current.hasMoved;
      handleDragEnd();
      if (!wasDragged) {
        handleIconClickRef.current();
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [handleDragMove, handleDragEnd]);

  // 셸/플로팅 두 경로의 목차 링크 항목(li>a>span+badge)이 a className 토큰만 다르고
  // 나머지 마크업은 동일하므로 한 곳에서 렌더한다. anchorClassName 은 각 호출부에서
  // 기존과 동일한 cn() 결과를 그대로 전달한다(출력 동일).
  const renderTocLink = useCallback((item: TocItem, anchorClassName: string) => (
    <li key={item.id}>
      <a
        href={`#${item.id}`}
        onClick={(e) => handleClick(e, item.id)}
        className={anchorClassName}
      >
        <span className="line-clamp-2">
          {item.text}
          {item.badge && (
            <span
              className={cn(
                "ml-1 inline-flex items-center rounded-md border px-1.5 py-0 text-[10px] font-semibold leading-4 align-middle",
                SECTION_LEVEL_STYLES[item.badge]
              )}
            >
              {item.badge}
            </span>
          )}
        </span>
      </a>
    </li>
  ), [handleClick]);

  const mobileBottomTool = useMemo(() => !inShell && !shouldUseProblemListInstead && items.length > 0 ? (
    <MobileBottomToolRegistration
      tool={{
        id: "toc",
        kind: "panel",
        label: "목차",
        icon: List,
        order: 5,
        ariaLabel: "목차 패널 열기",
        sheet: {
          title: "목차",
          resizable: true,
          defaultHeightRatio: 0.3,
          minHeightRatio: 0.3,
          maxHeightRatio: 0.85,
          content: (
            <ul className="space-y-1.5 text-sm">
              {items.map((item) =>
                renderTocLink(
                  item,
                  cn(
                    "block border-l-2 py-1 pl-3 transition-colors duration-200 hover:text-foreground",
                    item.level === 3 && "pl-7",
                    activeId === item.id
                      ? "border-amber-500 font-medium text-amber-600 dark:text-amber-400"
                      : "border-transparent text-muted-foreground",
                  )
                )
              )}
            </ul>
          ),
        },
      }}
    />
  ) : null, [inShell, shouldUseProblemListInstead, items, activeId, renderTocLink]);

  // 3컬럼 셸 안: 목차 리스트 <ul> 만 좌측 사이드바 목차 Section 본문(tocSlot)에 portal.
  // Section 래퍼/펼침 상태/세로 스플릿 높이는 ArticleShell 이 소유한다. 빈 목차일 땐 안내
  // 문구를 portal 해 빈 Section 을 방지. 슬롯이 아직 없으면 null(마운트 직전 깜빡임 방지).
  if (inShell) {
    if (!isCurrentPage) return null;
    if (!hasDesktopTocAccess) return null;
    if (!tocSlot) return null;
    return createPortal(
      items.length ? (
        <ul className="space-y-1 text-[13px]">
          {items.map((item) =>
            renderTocLink(
              item,
              cn(
                "block border-l-2 py-1 pl-3 transition-colors duration-200 hover:text-foreground",
                item.level === 3 && "pl-7",
                activeId === item.id
                  ? "border-amber-500 font-medium text-amber-600 dark:text-amber-400"
                  : "border-transparent text-muted-foreground",
              )
            )
          )}
        </ul>
      ) : (
        <p className="px-1 py-3 text-center text-[12.5px] text-muted-foreground">
          이 글에는 목차가 없어요.
        </p>
      ),
      tocSlot,
    );
  }

  // 비-셸(플로팅) 경로: 헤딩이 없으면 아무것도 그리지 않는다.
  if (!isCurrentPage || items.length === 0) {
    return null;
  }

  if (!hasDesktopTocAccess) {
    return <>{mobileBottomTool}</>;
  }

  const showFullToc = forcedOpen || (hasSpace && isVisible);

  // 숨김 상태 또는 공간 부족: 플로팅 아이콘 버튼
  if (!showFullToc) {
    return (
      <>
        {mobileBottomTool}
        <div className="hidden lg:contents">
          <button
            type="button"
            ref={iconRef}
            onMouseDown={onMouseDown}
            className="fixed z-[3] bg-white/90 dark:bg-neutral-900/90 backdrop-blur-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-2.5 rounded-lg shadow-sm transition-colors select-none cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            style={iconPosition
              ? { left: iconPosition.x, top: iconPosition.y }
              : articleRect ? { right: `${articleRect.viewportWidth - articleRect.left + 40}px`, top: 160 } : { left: 16, top: 160 }
            }
            aria-label="목차 보이기"
          >
            <List className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {mobileBottomTool}
      <nav
        className="fixed top-40 z-[3] hidden max-h-[calc(100vh_-_200px)] w-40 flex-col rounded-lg bg-white/90 p-3 shadow-sm backdrop-blur-sm dark:bg-neutral-900/90 lg:flex 2xl:w-52 2xl:p-4"
        style={{ right: `${tocRight}px` }}
        aria-label="목차"
      >
        <div className="mb-3 flex flex-shrink-0 items-center justify-between">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            목차
          </p>
          <button
            type="button"
            onClick={() => {
              if (hasSpace) {
                toggleVisibility();
              } else {
                setForcedOpen(false);
              }
            }}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            aria-label="목차 숨기기"
          >
            숨기기
          </button>
        </div>
        <ul className="space-y-1.5 text-sm overflow-y-auto flex-1 scrollbar-thin">
          {items.map((item) =>
            renderTocLink(
              item,
              cn(
                "block py-1 transition-colors duration-200 hover:text-gray-900 dark:hover:text-white",
                item.level === 3 && "pl-4", // h3 들여쓰기
                activeId === item.id
                  ? "text-orange-500 dark:text-orange-400 font-medium border-l-2 border-orange-500 dark:border-orange-400 pl-3"
                  : "text-gray-500 dark:text-gray-400 border-l-2 border-transparent pl-3",
                item.level === 3 && activeId === item.id && "pl-7", // h3 + 활성화 상태에서 들여쓰기 조정
                item.level === 3 && activeId !== item.id && "pl-7" // h3 비활성화 상태에서도 들여쓰기 유지
              )
            )
          )}
        </ul>
      </nav>
    </>
  );
}
